import { z } from 'zod';
import { SportNameSchema, type SportName } from './profile.js';
import { BADGE_NOTE_MAX } from './recommendation-catalog.js';

/**
 * F37 — Player ↔ Trainer Recommendations: the ENDORSEMENT surface
 * (trainer → player). Trainers award admin-catalog badges to players they
 * coached (D5 completed-session) or manage on a club team (D10 club-team);
 * the granting trainer can revoke (D7); the receiving player can hide per
 * badge (D8). Endorsements REUSE the F08 `user_badges` table (D15).
 */

// ─── Enums ───────────────────────────────────────────────────────────────────

export const BADGE_ATTRIBUTION_BASIS_VALUES = ['completed_session', 'club_team'] as const;
export type BadgeAttributionBasis = (typeof BADGE_ATTRIBUTION_BASIS_VALUES)[number];
export const BadgeAttributionBasisSchema = z.enum(BADGE_ATTRIBUTION_BASIS_VALUES);

// ─── Error codes ─────────────────────────────────────────────────────────────

export const ENDORSEMENT_ERROR_CODES = [
  'ENDORSEMENT_NOT_ELIGIBLE',
  'ENDORSEMENT_BADGE_NOT_GRANTABLE_FOR_SPORT',
  'ENDORSEMENT_CAP_REACHED',
  'ENDORSEMENT_DUPLICATE',
  'ENDORSEMENT_NOT_OWNER',
  'BADGE_NOT_FOUND',
  'CATALOG_ENTRY_IN_USE',
] as const;
export type EndorsementErrorCode = (typeof ENDORSEMENT_ERROR_CODES)[number];

// ─── Input schemas ───────────────────────────────────────────────────────────

export const AwardBadgeInputSchema = z
  .object({
    playerUserId: z.string().uuid(),
    badgeCatalogId: z.string().uuid(),
    sportName: SportNameSchema,
    note: z.string().max(BADGE_NOTE_MAX).optional(),
  })
  .strict();
export type AwardBadgeInput = z.infer<typeof AwardBadgeInputSchema>;

export const RevokeBadgeInputSchema = z
  .object({
    reason: z.string().max(500).optional(),
  })
  .strict();
export type RevokeBadgeInput = z.infer<typeof RevokeBadgeInputSchema>;

export const ToggleBadgeVisibilityInputSchema = z
  .object({
    visible: z.boolean(),
  })
  .strict();
export type ToggleBadgeVisibilityInput = z.infer<typeof ToggleBadgeVisibilityInputSchema>;

// ─── Read shapes ─────────────────────────────────────────────────────────────

/**
 * A player's gallery badge. The owner sees all (incl. hidden + revoked); the
 * public projection filters `visible && !revoked`. Anonymity is N/A for badges
 * (the granting trainer's attribution is always shown).
 */
export type PlayerBadge = {
  readonly id: string;
  readonly userId: string;
  readonly badgeCatalogId: string | null;
  readonly badgeCode: string;
  readonly badgeName: string;
  readonly badgeCategory: string;
  /** Resolves to a @bravemove/design-tokens/icons component (D13); null for legacy. */
  readonly iconCode: string | null;
  readonly sportName: SportName | null;
  readonly note: string | null;
  readonly visible: boolean;
  readonly attribution: {
    readonly trainerUserId: string | null;
    readonly trainerDisplayName: string | null;
    readonly label: string | null;
    readonly basis: BadgeAttributionBasis | null;
  };
  readonly awardedAt: string;
  readonly revoked: boolean;
  readonly revokedAt: string | null;
  readonly revokedReason: string | null;
};
